<template>
  <div class="tw-flex tw-flex-col tw-h-full">
    <FancyServerTable
      v-if="isStateLoaded"
      ref="fancyTableRef"
      :data="customersTableData"
      :columns="customersTableHeaders"
      :enable-filters="true"
      :enable-pagination="true"
      :enable-column-visibility="true"
      :enable-column-pinning="true"
      :initial-column-visibility="currentSavedState?.columnVisibility || {}"
      :initial-column-order="currentSavedState?.columnOrder || []"
      :initial-column-sizing="currentSavedState?.columnSizing || {}"
      :initial-column-filters="currentSavedState?.columnFilters || []"
      :initial-sorting="currentSavedState?.sorting ?? []"
      :initial-column-pinning="currentSavedState?.columnPinning || { left: [], right: ['actions'] }"
      :total-rows="totalRows"
      :page-index="pageIndex"
      :page-size="pageSize"
      :auto-filter="true"
      @update:row-selection="handleRowSelectionChange"
      :loading="isLoading"
      @update:state="debouncedHandleTableStateChange"
      @fetch-data="fetchCustomers"
    >
      <template #config-manager>
        <FancyTableConfigManager
          :saved-configs="savedTableConfigs"
          :current-config="currentSavedState"
          :is-loading="false"
          :can-save-state="canSaveState"
          @save-config="handleSaveConfig"
          @update-config="handleUpdateConfig"
          @apply-config="handleApplyConfig"
          @delete-config="handleDeleteConfig"
          @mark-as-default="handleMarkAsDefault"
        />
      </template>
    </FancyServerTable>
  </div>
</template>

<script setup lang="ts">
import { h, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  FancyServerTable,
  type FancyTableColumnDef,
  type ServerTableOptions,
  useTableRenderers,
  FieldType,
  FilterType,
  FancyTableConfigManager,
} from '@/components/shared/fancyTables';

import CustomersTableActions from './CustomersTableActions.vue';
import { useCustomerStore } from '@/stores/customer.store';
import { useFancyTableConfig } from '@/_helpers/composables/fancy-table-config.composable';

const fancyTableRef = ref<any>(null);

// Map filters to API params
const mapFiltersToApiParams = (filters: any[]) => {
  const params: any = {};

  filters.forEach(filter => {
    const { id, value } = filter;

    switch (id) {
      case 'name':
        if (value) params.name = value;
        break;
      case 'legal_name':
        if (value) params.legal_name = value;
        break;
      case 'customer_limit':
        if (value?.start !== undefined) params.customer_limit_min = value.start;
        if (value?.end !== undefined) params.customer_limit_max = value.end;
        break;
      case 'auto_approval_limit':
        if (value?.start !== undefined) params.auto_approval_limit_min = value.start;
        if (value?.end !== undefined) params.auto_approval_limit_max = value.end;
        break;
      case 'financial_limit_frequency':
        if (value && value.length > 0) params.financial_limit_frequency = value.join(',');
        break;
      case 'is_active':
        if (value !== undefined) {
          params.is_active = value ? 'true' : 'false';
        }
        break;
      default:
        break;
    }
  });

  return params;
};

// Map sorting to API param
const mapSortingToApiParam = (sorting: any[]): string => {
  if (sorting.length === 0) return '';

  const sort = sorting[0];
  const { id, desc } = sort;
  let backendField = id;

  switch (id) {
    case 'name':
      backendField = 'name';
      break;
    case 'legal_name':
      backendField = 'legal_name';
      break;
    case 'customer_limit':
      backendField = 'customer_limit_sort';
      break;
    case 'auto_approval_limit':
      backendField = 'auto_approval_limit_sort';
      break;
    case 'financial_limit_frequency':
      backendField = 'financial_limit_frequency';
      break;
    case 'is_active':
      backendField = 'is_active';
      break;
    default:
      backendField = id;
  }

  return desc ? `-${backendField}` : backendField;
};

// Server-side fetch function
const fetchCustomers = async (options?: ServerTableOptions) => {
  isLoading.value = true;

  try {
    const currentPageIndex = options?.pageIndex ?? 0;
    const currentPageSize = options?.pageSize ?? 10;
    const filters = options?.filters ?? [];
    const sorting = options?.sorting ?? [];

    // Update pagination state
    pageIndex.value = currentPageIndex;
    pageSize.value = currentPageSize;

    // Map filters to API params
    const filterParams = mapFiltersToApiParams(filters);

    // Map sorting to API param
    const orderingParam = mapSortingToApiParam(sorting);

    const apiParams = {
      page: currentPageIndex + 1,
      page_size: currentPageSize,
      ...filterParams,
      ...(orderingParam && { ordering: orderingParam }),
    };

    const response = await getCustomers(apiParams);

    customersTableData.value = response.results;
    totalRows.value = response.count;
  } catch (error) {
    console.error('Error fetching customers:', error);
    customersTableData.value = [];
    totalRows.value = 0;
  } finally {
    isLoading.value = false;
  }
};

const getCustomersTableList = async () => {
  // This is now a wrapper for fetchCustomers for backward compatibility
  await fetchCustomers();
};

const {
  savedTableConfigs,
  currentSavedState,
  canSaveState,
  handleSaveConfig,
  handleUpdateConfig,
  handleApplyConfig,
  debouncedHandleTableStateChange,
  handleDeleteConfig,
  handleMarkAsDefault,
  isStateLoaded,
} = useFancyTableConfig({
  tableName: 'customers',
  fancyTableRef: fancyTableRef,
  tableLoader: getCustomersTableList,
});

const selectedCustomers = ref<any[]>([]);
const { GetFieldRenderer } = useTableRenderers();

const { t } = useI18n();

const isLoading = ref(false);
const { getCustomers } = useCustomerStore();

const customersTableData = ref<any[]>([]);
const totalRows = ref(0);
const pageIndex = ref(0);
const pageSize = ref(10);

const frequencyOptions = [
  {
    label: t('customer_management.main_data.frequency_weekly'),
    value: 'WEEKLY',
  },
  {
    label: t('customer_management.main_data.frequency_monthly'),
    value: 'MONTHLY',
  },
  {
    label: t('customer_management.main_data.frequency_yearly'),
    value: 'YEARLY',
  },
  {
    label: t('customer_management.main_data.frequency_all_time'),
    value: 'ALL_TIME',
  },
];

const customersTableHeaders: FancyTableColumnDef<any>[] = [
  {
    header: t('customer_management.titles.name'),
    accessorKey: 'name',
    enableSorting: true,
    enableColumnFilter: true,
    enableHiding: false,
    fieldType: FieldType.LINK_INTERNAL,
    cell: GetFieldRenderer(FieldType.LINK_INTERNAL, {
      link: {
        name: 'view-customer',
        key: 'id',
      },
    }),
    size: 200,
  },
  {
    header: t('customer_management.forms.legal_name'),
    accessorKey: 'legal_name',
    enableSorting: true,
    enableColumnFilter: true,
    enableHiding: true,
    fieldType: FieldType.TEXT,
    cell: GetFieldRenderer(FieldType.TEXT),
    size: 200,
  },
  {
    header: t('customer_management.forms.customer_limit'),
    accessorKey: 'customer_limit',
    enableSorting: true,
    enableColumnFilter: true,
    enableHiding: true,
    fieldType: FieldType.MONEY,
    filterType: FilterType.NUMBER_RANGE,
    filterFn: 'inNumberRange',
    sortingFn: 'money',
    cell: GetFieldRenderer(FieldType.MONEY),
    size: 200,
  },
  {
    header: t('customer_management.assign_team.auto_approval_limit_amount'),
    accessorKey: 'auto_approval_limit',
    enableSorting: true,
    enableColumnFilter: true,
    enableHiding: true,
    fieldType: FieldType.MONEY,
    filterType: FilterType.NUMBER_RANGE,
    cell: GetFieldRenderer(FieldType.MONEY),
    filterFn: 'inNumberRange',
    sortingFn: 'money',
    size: 200,
  },
  {
    header: t('customer_management.main_data.financial_limit_frequency'),
    accessorKey: 'financial_limit_frequency',
    enableSorting: true,
    enableColumnFilter: true,
    enableHiding: true,
    fieldType: FieldType.TEXT,
    filterType: FilterType.MULTI_SELECT,
    filterFn: 'enumFilter',
    cell: GetFieldRenderer(FieldType.ENUM, {
      enumOptions: frequencyOptions,
    }),
    filterOptions: frequencyOptions,
    size: 150,
  },
  {
    header: t('customer_management.titles.active'),
    accessorKey: 'is_active',
    enableSorting: true,
    enableColumnFilter: true,
    enableHiding: true,
    fieldType: FieldType.BOOLEAN,
    filterType: FilterType.BOOLEAN,
    filterFn: 'boolean',
    cell: GetFieldRenderer(FieldType.BOOLEAN),
    size: 150,
  },
  {
    header: t('common.headers.actions'),
    accessorKey: 'actions',
    enableSorting: false,
    enableColumnFilter: false,
    enableHiding: false,
    hideFromColumnConfig: true,
    cell: info => {
      return h(CustomersTableActions, {
        customer: info.row.original,
      });
    },
    size: 100,
  },
];

const handleRowSelectionChange = (selection: any[]) => {
  selectedCustomers.value = selection;
};
</script>
